import React, { useState, useEffect } from 'react';
import { Occasion, Payment } from '../types';
import { paymentsService } from '../services/paymentsService';
import ViewOccasionModal from './ViewOccasionModal';

interface OccasionsTableProps {
  occasions: Occasion[];
  onEdit: (occasion: Occasion) => void;
  onDelete: (id: string) => void;
  onUpdate?: (occasion: Occasion) => void;
}

interface OccasionWithPayments extends Occasion {
  payments?: Payment[];
  paymentsLoading?: boolean;
  paymentsError?: string;
}

const OccasionsTable: React.FC<OccasionsTableProps> = ({ occasions, onEdit, onDelete, onUpdate }) => {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [occasionsWithPayments, setOccasionsWithPayments] = useState<OccasionWithPayments[]>([]);
  const [viewingOccasion, setViewingOccasion] = useState<Occasion | null>(null);

  useEffect(() => {
    setOccasionsWithPayments(occasions.map(occasion => ({
      ...occasion,
      payments: [],
      paymentsLoading: false,
      paymentsError: ''
    })));
  }, [occasions]);

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const formatCurrency = (amount: number | string) => {
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    return isNaN(numAmount) ? amount : `SAR${numAmount.toFixed(2)}`;
  };

  const toggleRowExpansion = async (occasionId: string) => {
    const isExpanded = expandedRows.has(occasionId);
    
    if (isExpanded) {
      // Collapse row
      setExpandedRows(prev => {
        const newSet = new Set(prev);
        newSet.delete(occasionId);
        return newSet;
      });
    } else {
      // Expand row and load payments
      setExpandedRows(prev => new Set(prev).add(occasionId));
      
      // Update loading state
      setOccasionsWithPayments(prev => 
        prev.map(occasion => 
          occasion.occasionId === occasionId 
            ? { ...occasion, paymentsLoading: true, paymentsError: '' }
            : occasion
        )
      );

      try {
        const payments = await paymentsService.getPaymentsByOccasionId(occasionId);
        setOccasionsWithPayments(prev => 
          prev.map(occasion => 
            occasion.occasionId === occasionId 
              ? { ...occasion, payments, paymentsLoading: false }
              : occasion
          )
        );
      } catch (error) {
        setOccasionsWithPayments(prev => 
          prev.map(occasion => 
            occasion.occasionId === occasionId 
              ? { ...occasion, paymentsError: 'Failed to load payments', paymentsLoading: false }
              : occasion
          )
        );
      }
    }
  };

  const handleView = (occasion: Occasion) => {
    setViewingOccasion(occasion);
  };

  const handleUpdate = async (updatedOccasion: Occasion) => {
    if (onUpdate) {
      await onUpdate(updatedOccasion);
    }
    setViewingOccasion(null);
  };

  const handleCloseView = () => {
    setViewingOccasion(null);
  };

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{
        width: '100%',
        borderCollapse: 'collapse',
        fontSize: '14px'
      }}>
        <thead>
          <tr style={{ backgroundColor: '#f8f9fa' }}>
            <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>
              Occasion Name
            </th>
            <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>
              Type
            </th>
            <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>
              Person Name
            </th>
            <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>
              Date
            </th>
            <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>
              Gift Amount
            </th>
            <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>
              Status
            </th>
            <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>
              Payments
            </th>
            <th style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #dee2e6' }}>
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {occasionsWithPayments.length === 0 ? (
            <tr>
              <td colSpan={8} style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
                No occasions found
              </td>
            </tr>
          ) : (
            occasionsWithPayments.map((occasion) => (
              <React.Fragment key={occasion.occasionId}>
                <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                  <td style={{ padding: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <button
                        onClick={() => toggleRowExpansion(occasion.occasionId)}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: '16px',
                          color: '#666',
                          padding: '4px'
                        }}
                      >
                        {expandedRows.has(occasion.occasionId) ? '▼' : '▶'}
                      </button>
                      <div>
                        <div style={{ fontWeight: '500' }}>
                          {occasion.occasionName || 'Unnamed Occasion'}
                        </div>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          ID: {occasion.occasionId}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '12px' }}>
                    <div>{occasion.type || occasion.occasionType || 'N/A'}</div>
                  </td>
                  <td style={{ padding: '12px' }}>
                    <div>{occasion.personName || 'N/A'}</div>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      {occasion.personEmail}
                    </div>
                  </td>
                  <td style={{ padding: '12px' }}>
                    {formatDate(occasion.occasionDate)}
                  </td>
                  <td style={{ padding: '12px' }}>
                    <div>{formatCurrency(occasion.moneyGiftAmount)}</div>
                    {occasion.giftPrice && (
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        Gift: {formatCurrency(occasion.giftPrice)}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '12px' }}>
                    <div style={{
                      display: 'inline-block',
                      padding: '4px 8px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: '500',
                      backgroundColor: occasion.isActive !== false ? '#d4edda' : '#f8d7da',
                      color: occasion.isActive !== false ? '#155724' : '#721c24'
                    }}>
                      {occasion.isActive !== false ? 'Active' : 'Inactive'}
                    </div>
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                      {occasion.isPrivate ? 'Private' : 'Public'}
                    </div>
                  </td>
                  <td style={{ padding: '12px' }}>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      {(occasion as any).paymentCount || occasion.payments?.length || 0} payments
                    </div>
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center' }}>
                    <div style={{ display: 'flex', gap: '6px', justifyContent: 'center', flexWrap: 'wrap' }}>
                      <button
                        onClick={() => handleView(occasion)}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: '#17a2b8',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        View
                      </button>
                      {/*
                      <button
                        onClick={() => onEdit(occasion)}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: '#007bff',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        Edit
                      </button>
                      */}
                      <button
                        onClick={() => onDelete(occasion.occasionId)}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: '#dc3545',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
                
                {/* Payments row */}
                {expandedRows.has(occasion.occasionId) && (
                  <tr style={{ backgroundColor: '#f8f9fa' }}>
                    <td colSpan={8} style={{ padding: '0' }}>
                      <div style={{ padding: '1rem' }}>
                        <h4 style={{ margin: '0 0 1rem 0', color: '#333' }}>Payments</h4>
                        
                        {occasion.paymentsLoading ? (
                          <div style={{ textAlign: 'center', padding: '1rem', color: '#666' }}>
                            Loading payments...
                          </div>
                        ) : occasion.paymentsError ? (
                          <div style={{ 
                            color: '#dc3545', 
                            padding: '1rem', 
                            backgroundColor: '#f8d7da',
                            borderRadius: '4px',
                            border: '1px solid #f5c6cb'
                          }}>
                            {occasion.paymentsError}
                          </div>
                        ) : occasion.payments && occasion.payments.length > 0 ? (
                          <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', fontSize: '14px' }}>
                              <thead>
                                <tr style={{ backgroundColor: '#e9ecef' }}>
                                  <th style={{ padding: '8px', textAlign: 'left' }}>Payment ID</th>
                                  <th style={{ padding: '8px', textAlign: 'left' }}>Amount</th>
                                  <th style={{ padding: '8px', textAlign: 'left' }}>Status</th>
                                  <th style={{ padding: '8px', textAlign: 'left' }}>Date</th>
                                  <th style={{ padding: '8px', textAlign: 'left' }}>Person</th>
                                  <th style={{ padding: '8px', textAlign: 'left' }}>Transaction ID</th>
                                </tr>
                              </thead>
                              <tbody>
                                {occasion.payments.map((payment) => (
                                  <tr key={payment.paymentId} style={{ borderBottom: '1px solid #dee2e6' }}>
                                    <td style={{ padding: '8px' }}>
                                      <div style={{ fontSize: '12px', color: '#666' }}>
                                        {payment.paymentId}
                                      </div>
                                    </td>
                                    <td style={{ padding: '8px' }}>
                                      <div style={{ fontWeight: '500' }}>
                                        {formatCurrency(payment.paymentAmount)}
                                      </div>
                                    </td>
                                    <td style={{ padding: '8px' }}>
                                      <div style={{
                                        display: 'inline-block',
                                        padding: '2px 6px',
                                        borderRadius: '8px',
                                        fontSize: '11px',
                                        fontWeight: '500',
                                        backgroundColor: payment.paymentStatus === 'success' ? '#d4edda' : '#f8d7da',
                                        color: payment.paymentStatus === 'success' ? '#155724' : '#721c24'
                                      }}>
                                        {payment.paymentStatus}
                                      </div>
                                    </td>
                                    <td style={{ padding: '8px' }}>
                                      {formatDate(payment.paymentDate)}
                                    </td>
                                    <td style={{ padding: '8px' }}>
                                      <div>{payment.personName || 'N/A'}</div>
                                      <div style={{ fontSize: '12px', color: '#666' }}>
                                        {payment.personPhone}
                                      </div>
                                    </td>
                                    <td style={{ padding: '8px' }}>
                                      <div style={{ fontSize: '12px', color: '#666' }}>
                                        {payment.transactionId}
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <div style={{ textAlign: 'center', padding: '1rem', color: '#666' }}>
                            No payments found for this occasion
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))
          )}
        </tbody>
      </table>

      {/* View Occasion Modal */}
      {viewingOccasion && (
        <ViewOccasionModal
          occasion={viewingOccasion}
          onClose={handleCloseView}
          onEdit={onUpdate}
        />
      )}
    </div>
  );
};

export default OccasionsTable;
